/** Click-through strategy evidence. Mock layout using existing backtest /
 * regime-fit field names. Neutral presentation — a high win rate is not green.
 *
 * Live mode reuses this chrome for historical research only. It does not bind
 * legs, premiums, weekly economics, observed pricing, or basket action.
 */
import { useEffect, useState } from "react";
import { HistoricalOutcomesChart } from "@/workspace/HistoricalOutcomesChart";
import { EarningsQualityBadge, KeyRow, SectionTitle, WrapStatus, useIsNarrow } from "@/workspace/primitives";
import { EvidenceConditionsPanel } from "@/workspace/EvidenceConditionsPanel";
import { NeutralStat, RegimeContextTable, liveMetricValue, pct, ror, usd } from "@/workspace/historicalResearchChrome";
import { BASKET, HELPER } from "@/workspace/screenLanguage";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";
import { RESEARCH_FOCUS_KIND } from "@/workspace/live/workspaceLiveTypes";
import { earningsConflict } from "@/workspace/mockTickerData";

const FIT = {
  available: { tier: "info", text: "Historical regime context available" },
  differs: { tier: "caution", text: "Historical regime context differs" },
  limited: { tier: "info", text: "Historical regime context limited" },
};

const STATE = {
  clear: { tier: "info", text: "Basket action available" },
  flag: { tier: "caution", text: "Indicative estimate only" },
  unavailable: { tier: "blocking", text: "Basket action unavailable" },
};

const BASIS = {
  observed: { tier: "info", text: "Observed pricing available" },
  partial: { tier: "blocking", text: "Required quote fields unavailable" },
  modelled: { tier: "caution", text: "Indicative estimate only" },
  lastPrice: { tier: "caution", text: "Indicative estimate only" },
  unverified: { tier: "blocking", text: "Option quote timestamp unavailable" },
  unavailable: { tier: "blocking", text: "Pricing unavailable" },
};

function historicalLayer(kind) {
  if (kind === RESEARCH_FOCUS_KIND.ready) {
    return { mark: "●", text: LIVE_UNAVAILABLE.railHistoricalAvailable, tier: "info" };
  }
  if (kind === RESEARCH_FOCUS_KIND.limited) {
    return { mark: "○", text: LIVE_UNAVAILABLE.railHistoricalLimited, tier: "caution" };
  }
  if (kind === RESEARCH_FOCUS_KIND.unavailable) {
    return { mark: "○", text: LIVE_UNAVAILABLE.railHistoricalUnavailable, tier: "caution" };
  }
  if (kind === RESEARCH_FOCUS_KIND.pending) {
    return { mark: "○", text: "Loading historical strategy focus.", tier: "info" };
  }
  return { mark: "○", text: LIVE_UNAVAILABLE.railHistoricalChoose, tier: "info" };
}

function LiveResearchEvidence({ ticker, researchFocus, currentQuote }) {
  const narrow = useIsNarrow();
  const [open, setOpen] = useState(!narrow);
  useEffect(() => { setOpen(!narrow); }, [narrow]);
  const focus = researchFocus || ticker.researchFocus || { kind: RESEARCH_FOCUS_KIND.choose };
  const kind = focus.kind;
  const showReady = kind === RESEARCH_FOCUS_KIND.ready || kind === RESEARCH_FOCUS_KIND.limited;
  const metrics = focus.metrics || {};
  const labels = LIVE_UNAVAILABLE.historicalMetricLabels;
  const current = currentQuote || ticker.currentQuote || {};
  const hist = historicalLayer(kind);
  const currentCol = (focus.regimeTable || []).find((c) => c.current);
  const confirmed = ticker.regime?.confirmedState || currentCol?.label || "—";
  const exp = (ticker.availableExpirations || []).find((e) => e.date === ticker.defaultExpiryDate)
    || ticker.availableExpirations?.[0];
  const conflict = earningsConflict(ticker.earnings, exp?.dte);
  const quoteBadge = current.availability === "unverified_timestamp"
    ? LIVE_UNAVAILABLE.currentBadgeUnverified
    : LIVE_UNAVAILABLE.currentBadgeUnavailable;

  return (
    <aside
      className="border p-3 min-w-0 space-y-3"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}
      data-testid="strategy-evidence"
      data-strategy={focus.strategyId || ""}
    >
      <header>
        <div className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
          {showReady ? focus.displayName : "Research focus"}
        </div>
        <div className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
          {showReady && focus.familyDisplay ? `Registry family: ${focus.familyDisplay}` : LIVE_UNAVAILABLE.historicalExplainer}
        </div>
        <div className="space-y-1.5 mt-1.5 text-[10px]" data-testid="evidence-status-layers">
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>A · Data / quote · </span>
            <WrapStatus tier="blocking" text={quoteBadge} />
          </div>
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>B · Event / parameter · </span>
            <WrapStatus tier={conflict?.state === "inside" ? "caution" : "info"} text={conflict?.label || "Earnings date unavailable"} />
          </div>
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>C · Product action · </span>
            <WrapStatus tier="blocking" text={LIVE_UNAVAILABLE.currentActionUnavailable} />
          </div>
        </div>
      </header>

      {/* Every historical metric surface lives here, inside the rail, exactly once. */}
      <div className="space-y-3" data-testid="options-historical-context">
      <section data-testid="evidence-structure">
        <SectionTitle>Research focus</SectionTitle>
        {kind === RESEARCH_FOCUS_KIND.choose ? (
          <p className="text-[11px] leading-snug" data-testid="research-choose">{LIVE_UNAVAILABLE.researchChoose}</p>
        ) : null}
        {kind === RESEARCH_FOCUS_KIND.pending ? (
          <p className="text-[11px] leading-snug" data-testid="research-pending">Loading historical strategy focus.</p>
        ) : null}
        {kind === RESEARCH_FOCUS_KIND.unavailable ? (
          <div data-testid="research-unavailable">
            <p className="text-[11px] leading-snug">{LIVE_UNAVAILABLE.researchUnavailableTitle}</p>
            <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.researchUnavailableDetail}</p>
          </div>
        ) : null}
        {showReady ? (
          <>
            <KeyRow label="Research focus">
              <span data-testid="research-focus-name">{focus.displayName}</span>
            </KeyRow>
            <KeyRow label="Family">{focus.familyDisplay}</KeyRow>
            <KeyRow label="Historical model">
              <span data-testid="research-simulation-label">{focus.simulationLabel || LIVE_UNAVAILABLE.researchSimulationLabel}</span>
            </KeyRow>
            <KeyRow label="Product action">{LIVE_UNAVAILABLE.currentActionUnavailable}</KeyRow>
          </>
        ) : null}
      </section>

      <section data-testid="evidence-historical">
        <SectionTitle>Historical evidence</SectionTitle>
        <p className="text-[10px] mb-1.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="options-historical-explainer">
          {LIVE_UNAVAILABLE.historicalExplainer}
        </p>
        <div className="grid grid-cols-2 gap-2" data-testid="options-historical-scalars">
          <NeutralStat label={labels.win_rate}>{liveMetricValue(showReady ? metrics.win_rate : null)}</NeutralStat>
          <NeutralStat label={labels.avg_return_on_risk}>{liveMetricValue(showReady ? metrics.avg_return_on_risk : null)}</NeutralStat>
          <NeutralStat label={labels.sample_size}>{liveMetricValue(showReady ? metrics.sample_size : null)}</NeutralStat>
          <NeutralStat label={labels.profit_factor}>{liveMetricValue(showReady ? metrics.profit_factor : null)}</NeutralStat>
          <NeutralStat label={labels.max_loss}>{liveMetricValue(showReady ? metrics.max_loss : null)}</NeutralStat>
          {showReady && metrics.assignmentSupported ? (
            <NeutralStat label={labels.assignment_rate}>{liveMetricValue(metrics.assignment_rate)}</NeutralStat>
          ) : (
            <NeutralStat label="Confirmed regime">{confirmed}</NeutralStat>
          )}
        </div>
        {kind === RESEARCH_FOCUS_KIND.limited ? (
          <p className="text-[10px] mt-1.5" style={{ color: "var(--hoip-text-secondary)" }} data-testid="research-limited">
            {LIVE_UNAVAILABLE.researchLimited}
          </p>
        ) : null}
        <div className="mt-2">
          <SectionTitle>Historical trade outcomes</SectionTitle>
          <p className="text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
            {LIVE_UNAVAILABLE.perTradeChart}
          </p>
        </div>
      </section>

      {showReady ? (
        <section data-testid="evidence-regime-context">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center justify-between w-full text-left"
            data-testid="regime-context-toggle"
          >
            <SectionTitle>Regime Context</SectionTitle>
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{open ? "Hide" : "Show"}</span>
          </button>
          <div className={open ? "" : "hidden"}>
            <p className="text-[11px] mb-1.5" style={{ color: "var(--hoip-text-secondary)" }}>
              Current confirmed regime: {confirmed}
              {currentCol?.sample_size != null ? ` · n=${currentCol.sample_size}` : ""}
            </p>
            {currentCol?.insufficient ? (
              <p className="text-[11px] mb-1.5" style={{ color: "var(--hoip-text-secondary)" }} data-testid="insufficient-regime-sample">
                Limited historical evidence. Sample size is below the minimum threshold.
              </p>
            ) : currentCol && !currentCol.insufficient ? (
              <div className="grid grid-cols-2 gap-2 mb-2">
                <NeutralStat label="Win rate">{liveMetricValue(currentCol.win_rate)}</NeutralStat>
                <NeutralStat label="Average RoR/trade">{liveMetricValue(currentCol.avg_return_on_risk)}</NeutralStat>
              </div>
            ) : null}
            <RegimeContextTable
              columns={focus.regimeTable || []}
              confirmedState={confirmed}
              valueMode="live"
              testid="research-regime-table"
            />
          </div>
        </section>
      ) : null}

      <section data-testid="evidence-regime">
        <SectionTitle>Current-regime evidence</SectionTitle>
        <KeyRow label="Confirmed HMM regime">{ticker.regime?.confirmedState || LIVE_UNAVAILABLE.notInOutput}</KeyRow>
        <KeyRow label="Regime confidence">
          {ticker.regime?.confidence == null
            ? LIVE_UNAVAILABLE.notInOutput
            : `${Math.round((ticker.regime.confidence <= 1 ? ticker.regime.confidence * 100 : ticker.regime.confidence))}%`}
        </KeyRow>
        <div className="py-1"><WrapStatus tier={hist.tier} text={hist.text} /></div>
      </section>

      <p className="text-[10px] leading-snug pt-1 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }} data-testid="research-methodology">
        {focus.methodology || LIVE_UNAVAILABLE.researchMethodology}
      </p>
      </div>

      <EvidenceConditionsPanel
        ticker={ticker}
        researchFocus={focus}
        currentQuote={current}
      />
    </aside>
  );
}

export function StrategyEvidencePanel({ row, ticker, researchFocus, currentQuote }) {
  const narrow = useIsNarrow();
  const [open, setOpen] = useState(!narrow);
  useEffect(() => { setOpen(!narrow); }, [narrow]);

  if (ticker?.live) {
    return (
      <LiveResearchEvidence
        ticker={ticker}
        researchFocus={researchFocus || ticker.researchFocus}
        currentQuote={currentQuote || ticker.currentQuote}
      />
    );
  }

  if (!row) {
    return (
      <div
        className="border p-3 text-[11px]"
        style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)", color: "var(--hoip-text-tertiary)" }}
        data-testid="strategy-evidence-empty"
      >
        Select a structure to inspect historical and regime-conditioned evidence.
      </div>
    );
  }

  const ev = row.evidence;
  const hist = ev.historical;
  const reg = ev.regime;
  const ready = ev.readiness;
  const layers = row.layers;
  const state = STATE[ev.state] || STATE.flag;
  const basis = BASIS[ev.basis] || BASIS.modelled;
  const fit = FIT[reg.regime_fit] || FIT.limited;
  const currentCol = (ev.regimeContext || []).find((c) => c.current);
  const negativeRor = Number.isFinite(hist.avg_return_on_risk) && hist.avg_return_on_risk < 0;

  return (
    <aside
      className="border p-3 min-w-0 space-y-3"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}
      data-testid="strategy-evidence"
      data-strategy={row.strategy}
    >
      <header>
        <div className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
          {row.strategy}
        </div>
        <div className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{ev.family}</div>
        <div className="space-y-1.5 mt-1.5 text-[10px]" data-testid="evidence-status-layers">
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>A · Data / quote · </span>
            <WrapStatus tier={layers?.data?.tier || basis.tier} text={layers?.data?.text || basis.text} />
          </div>
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>B · Event / parameter · </span>
            <WrapStatus tier={layers?.event?.tier || "info"} text={layers?.event?.text || row.conflict?.label} />
          </div>
          <div>
            <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>C · Product action · </span>
            <WrapStatus tier={layers?.action?.tier || state.tier} text={layers?.action?.text || state.text} />
          </div>
        </div>
      </header>

      <section data-testid="evidence-structure">
        <SectionTitle>Selected structure</SectionTitle>
        <KeyRow label="Family">{ev.family}</KeyRow>
        <KeyRow label="Legs">
          {ev.legs.map((l) => `${l.side} ${l.strike}${l.right}${l.note ? ` (${l.note})` : ""}`).join(" · ")}
        </KeyRow>
        <KeyRow label="Expiry / DTE">{ev.expiry} · {ev.dte}d to expiry</KeyRow>
        <KeyRow label="Strike / width">{ev.width}</KeyRow>
        <KeyRow label="Pricing basis">{basis.text}</KeyRow>
        <KeyRow label="Product action">{layers?.action?.text || state.text}</KeyRow>
        <KeyRow label={ev.weekly?.label || "Weekly economics"}>{ev.weekly?.value ?? "Unavailable"}</KeyRow>
        {ev.weekly?.extraLabel ? (
          <KeyRow label={ev.weekly.extraLabel}>{ev.weekly.extraValue}</KeyRow>
        ) : null}
      </section>

      <section data-testid="evidence-historical">
        <SectionTitle>Historical evidence</SectionTitle>
        <div className="grid grid-cols-2 gap-2">
          <NeutralStat label="Historical win rate">{pct(hist.win_rate)}</NeutralStat>
          <NeutralStat label="Historical avg RoR / trade">{ror(hist.avg_return_on_risk)}</NeutralStat>
          <NeutralStat label="Observation count">{hist.sample_size}</NeutralStat>
          {hist.profit_factor != null ? (
            <NeutralStat label="Profit factor">{hist.profit_factor.toFixed(2)}</NeutralStat>
          ) : (
            <NeutralStat label="Profit factor">Not available in this output</NeutralStat>
          )}
          <NeutralStat label="Maximum historical loss">{usd(hist.max_loss)}</NeutralStat>
          <NeutralStat label="Defined-risk cap">{hist.defined_risk_cap != null ? usd(hist.defined_risk_cap) : "Undefined risk"}</NeutralStat>
          {hist.assignment_rate != null ? (
            <NeutralStat label="Assignment rate">{pct(hist.assignment_rate)}</NeutralStat>
          ) : null}
        </div>
        {negativeRor ? (
          <p className="text-[10px] mt-1.5" style={{ color: "var(--hoip-text-secondary)" }} data-testid="negative-ror-note">
            Historical result shown for context; not a forecast.
          </p>
        ) : null}
        <div className="mt-2">
          <SectionTitle>Historical trade outcomes</SectionTitle>
          {Array.isArray(hist.trades) && hist.trades.length ? (
            <HistoricalOutcomesChart
              trades={hist.trades}
              highlightRegime={reg.confirmedState}
              allowHighlight={!reg.insufficient}
              testid="historical-outcomes-chart"
            />
          ) : (
            <p className="text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
              Not available in current cached output
            </p>
          )}
        </div>
      </section>

      <section data-testid="evidence-regime-context">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center justify-between w-full text-left"
          data-testid="regime-context-toggle"
        >
          <SectionTitle>Regime Context</SectionTitle>
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{open ? "Hide" : "Show"}</span>
        </button>
        {open ? (
          <>
            <p className="text-[11px] mb-1.5" style={{ color: "var(--hoip-text-secondary)" }}>
              Current confirmed regime: {reg.confirmedState}
              {currentCol?.sample_size != null ? ` · Historical ${row.strategy} observations in ${reg.confirmedState}: n=${currentCol.sample_size}` : ""}
            </p>
            {currentCol?.insufficient ? (
              <p className="text-[11px] mb-1.5" style={{ color: "var(--hoip-text-secondary)" }} data-testid="insufficient-regime-sample">
                Insufficient regime sample — n={currentCol.sample_size}; no regime-conditioned performance statistic displayed.
              </p>
            ) : currentCol && !currentCol.insufficient ? (
              <div className="grid grid-cols-2 gap-2 mb-2">
                <NeutralStat label="Win rate">{pct(currentCol.win_rate)}</NeutralStat>
                <NeutralStat label="Average RoR/trade">{ror(currentCol.avg_return_on_risk)}</NeutralStat>
                {currentCol.assignmentSupported ? (
                  <NeutralStat label="Assignment rate">{pct(currentCol.assignment_rate)}</NeutralStat>
                ) : null}
              </div>
            ) : null}
            <RegimeContextTable columns={ev.regimeContext || []} confirmedState={reg.confirmedState} />
          </>
        ) : null}
      </section>

      <section data-testid="evidence-regime">
        <SectionTitle>Current-regime evidence</SectionTitle>
        <KeyRow label="Confirmed HMM regime">{reg.confirmedState}</KeyRow>
        <KeyRow label="Regime confidence">{Math.round(reg.confidence * 100)}%</KeyRow>
        <div className="py-1"><WrapStatus tier={fit.tier} text={fit.text} /></div>
      </section>

      <section data-testid="evidence-readiness">
        <SectionTitle>Selected-structure status</SectionTitle>
        <KeyRow label="Structure validity">{ready.structureValid ? "Observed" : "Unavailable"}</KeyRow>
        <KeyRow label="Quote completeness">{ready.quoteCompleteness}</KeyRow>
        <KeyRow label="Timestamp quality">{ready.timestampQuality}</KeyRow>
        <KeyRow label="Pricing method">{ready.pricingMethod}</KeyRow>
        <KeyRow label="Earnings state">
          <span className="inline-flex items-center gap-1.5">
            {ready.earningsConflict}
            {row.conflict?.quality ? <EarningsQualityBadge quality={row.conflict.quality} /> : null}
          </span>
        </KeyRow>
        <KeyRow label="Product action">{ready.liquidityState}</KeyRow>
        {ready.blockers.length ? (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {ready.blockers.map((b) => (
              <WrapStatus
                key={b}
                tier={b === BASKET?.unavailable || /unavailable/i.test(b) ? "blocking" : "caution"}
                text={b}
              />
            ))}
          </div>
        ) : null}
      </section>

      <p className="text-[10px] leading-snug pt-1 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
        {HELPER.evidence}
      </p>

      <EvidenceConditionsPanel ticker={ticker} row={row} />
    </aside>
  );
}
